// Enum-value -> human-readable label mappings for the LitigationEvents UI (Session 6B).
// Backend uses machine values (EventType.motion_to_dismiss_filed); lawyers need labels
// ("Motion to Dismiss (filed)"). Expand as new event types are added to app/taxonomy.

export const EVENT_TYPE_LABELS = {
  // Pleadings
  complaint_filed: 'Complaint (filed)',
  answer_filed: 'Answer (filed)',
  reply_filed: 'Reply (filed)',
  counterclaim_filed: 'Counterclaim (filed)',
  cross_claim_filed: 'Cross-claim (filed)',
  third_party_complaint_filed: 'Third-party Complaint (filed)',
  amended_pleading_filed: 'Amended Pleading (filed)',
  affirmative_defense_raised: 'Affirmative Defense (raised)',
  dependency_petition_filed: 'Dependency Petition (filed)',
  // Motions — dispositive
  motion_to_dismiss_filed: 'Motion to Dismiss (filed)',
  motion_for_summary_judgment_filed: 'Motion for Summary Judgment (filed)',
  motion_for_judgment_on_pleadings_filed: 'Motion for Judgment on the Pleadings (filed)',
  // Motions — discovery
  motion_to_compel_filed: 'Motion to Compel (filed)',
  motion_for_protective_order_filed: 'Motion for Protective Order (filed)',
  motion_for_sanctions_filed: 'Motion for Sanctions (filed)',
  // Motions — procedural
  motion_for_continuance_filed: 'Motion for Continuance (filed)',
  motion_to_amend_filed: 'Motion to Amend (filed)',
  motion_for_extension_filed: 'Motion for Extension of Time (filed)',
  // Discovery
  interrogatories_served: 'Interrogatories (served)',
  interrogatories_responded: 'Interrogatories (responded)',
  request_for_production_served: 'Request for Production (served)',
  request_for_production_responded: 'Request for Production (responded)',
  request_for_admissions_served: 'Requests for Admission (served)',
  request_for_admissions_responded: 'Requests for Admission (responded)',
  deposition_noticed: 'Deposition (noticed)',
  deposition_taken: 'Deposition (taken)',
  notice_of_discovery_filed: 'Notice of Discovery (filed)',
  discovery_exhibit_served: 'Discovery Exhibit (served)',
  // Criminal
  charge_filed: 'Formal Charge (information/indictment filed)',
  speedy_trial_demand_filed: 'Speedy Trial Demand (filed)',
  arrest_made: 'Arrest',
  first_appearance_held: 'First Appearance (held)',
  // Court events
  hearing_scheduled: 'Hearing (scheduled)',
  hearing_held: 'Hearing (held)',
  hearing_continued: 'Hearing (continued)',
  trial_scheduled: 'Trial (scheduled)',
  trial_day: 'Trial Day',
  settlement_conference_held: 'Settlement Conference (held)',
  mediation_scheduled: 'Mediation (scheduled)',
  mediation_held: 'Mediation (held)',
  shelter_hearing_held: 'Shelter Hearing (held)',
  child_sheltered: 'Child Placed in Shelter',
  // Rulings
  order_entered: 'Order (entered)',
  ruling_issued: 'Ruling (issued)',
  judgment_entered: 'Judgment (entered)',
  // Appellate
  notice_of_appeal_filed: 'Notice of Appeal (filed)',
  initial_brief_filed: 'Initial Brief (filed)',
  answer_brief_filed: 'Answer Brief (filed)',
  reply_brief_filed: 'Reply Brief (filed)',
  oral_argument_scheduled: 'Oral Argument (scheduled)',
  oral_argument_held: 'Oral Argument (held)',
  mandate_issued: 'Mandate (issued)',
  // Negotiation
  demand_made: 'Demand (made)',
  offer_made: 'Settlement Offer (made)',
  offer_accepted: 'Settlement Offer (accepted)',
  offer_rejected: 'Settlement Offer (rejected)',
  settlement_reached: 'Settlement Reached',
  // Other
  other: 'Other',
};

// Ordered groups for the entry-form dropdown (<optgroup>s).
export const EVENT_TYPE_GROUPS = [
  { label: 'Pleadings', types: ['complaint_filed', 'answer_filed', 'reply_filed', 'counterclaim_filed', 'cross_claim_filed', 'third_party_complaint_filed', 'amended_pleading_filed', 'affirmative_defense_raised', 'dependency_petition_filed'] },
  { label: 'Motions — Dispositive', types: ['motion_to_dismiss_filed', 'motion_for_summary_judgment_filed', 'motion_for_judgment_on_pleadings_filed'] },
  { label: 'Motions — Discovery', types: ['motion_to_compel_filed', 'motion_for_protective_order_filed', 'motion_for_sanctions_filed'] },
  { label: 'Motions — Procedural', types: ['motion_for_continuance_filed', 'motion_to_amend_filed', 'motion_for_extension_filed'] },
  { label: 'Discovery', types: ['interrogatories_served', 'interrogatories_responded', 'request_for_production_served', 'request_for_production_responded', 'request_for_admissions_served', 'request_for_admissions_responded', 'deposition_noticed', 'deposition_taken', 'notice_of_discovery_filed', 'discovery_exhibit_served'] },
  { label: 'Criminal', types: ['charge_filed', 'speedy_trial_demand_filed', 'arrest_made', 'first_appearance_held'] },
  { label: 'Court Events', types: ['hearing_scheduled', 'hearing_held', 'hearing_continued', 'trial_scheduled', 'trial_day', 'settlement_conference_held', 'mediation_scheduled', 'mediation_held', 'shelter_hearing_held', 'child_sheltered'] },
  { label: 'Rulings', types: ['order_entered', 'ruling_issued', 'judgment_entered'] },
  { label: 'Appellate', types: ['notice_of_appeal_filed', 'initial_brief_filed', 'answer_brief_filed', 'reply_brief_filed', 'oral_argument_scheduled', 'oral_argument_held', 'mandate_issued'] },
  { label: 'Negotiation', types: ['demand_made', 'offer_made', 'offer_accepted', 'offer_rejected', 'settlement_reached'] },
  { label: 'Other', types: ['other'] },
];

export const EVENT_ORIGIN_LABELS = {
  self_initiated: 'We initiated',
  opposing_initiated: 'Opposing party',
  court_initiated: 'Court',
  third_party_initiated: 'Third party',
  scheduled: 'Scheduled',
};

export const SERVICE_METHOD_LABELS = {
  email: 'E-service (email)',
  mail: 'U.S. Mail (+5 days)',
  hand_delivery: 'Hand delivery',
  leaving_with_attorney: 'Left with attorney (+5 days)',
  not_applicable: 'Not applicable',
};

// Event types where a follow-on hearing_date is meaningful (the field appears in the form).
export const HEARING_DATE_TYPES = new Set([
  'motion_for_summary_judgment_filed',
  'hearing_scheduled',
  'trial_scheduled',
  'mediation_scheduled',
  'oral_argument_scheduled',
]);

// Event types the court originates (smart default for event_origin).
const COURT_TYPES = new Set(['order_entered', 'ruling_issued', 'judgment_entered', 'mandate_issued', 'hearing_scheduled', 'trial_scheduled', 'oral_argument_scheduled']);
const THIRD_PARTY_TYPES = new Set(['arrest_made', 'child_sheltered']);

export function defaultOriginFor(eventType) {
  if (COURT_TYPES.has(eventType)) return 'court_initiated';
  if (THIRD_PARTY_TYPES.has(eventType)) return 'third_party_initiated';
  return 'self_initiated'; // lawyer logging their own action; easily changed in the dropdown
}

export function eventTypeLabel(v) { return EVENT_TYPE_LABELS[v] || v; }
export function originLabel(v) { return EVENT_ORIGIN_LABELS[v] || v; }
export function serviceMethodLabel(v) { return SERVICE_METHOD_LABELS[v] || v; }

// Human "deadline_type" -> label (computed_deadlines rows).
export const DEADLINE_TYPE_LABELS = {
  answer_due: 'Answer due',
  reply_due: 'Reply due',
  interrogatory_response_due: 'Interrogatory responses due',
  production_response_due: 'Production responses due',
  admissions_response_due: 'Admissions responses due',
  mandatory_disclosure_due: 'Mandatory disclosure due',
  summary_judgment_response_due: 'SJ response due',
  sj_response_due: 'SJ response due',
  notice_of_appeal_due: 'Notice of appeal due',
  shelter_hearing_due: 'Shelter hearing due',
  first_appearance_due: 'First appearance due',
  dependency_petition_due: 'Dependency petition due',
  speedy_trial_deadline: 'Speedy trial deadline',
};
export function deadlineTypeLabel(v) { return DEADLINE_TYPE_LABELS[v] || (v || '').replace(/_/g, ' '); }
